import { goToAnotherPage as goToAnotherPageIcon } from "@/designSystemIcons";
import { fireGTMEvent } from "@/functions/fireGTMEvent";
import { formatTime } from "@/functions/formatTime";
import { cn } from "@/lib/utils";
import { clubStatsSelector } from "@/redux/myClub/myClubSlice";
import { canUseClubImportSelector } from "@/redux/user/userSlice";
import { EXPERIMENT_EVENTS } from "@/settings";
import { useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

const SpaceCard = ({
  to,
  title,
  foregroundIcon,
  primaryStat,
  goToAnotherPage,
  onClick,
}: {
  to: string;
  title: string;
  foregroundIcon: string;
  primaryStat: { label: string; value: string | number };
  goToAnotherPage?: boolean;
  onClick?: () => void;
}) => {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className={cn(
        "relative overflow-hidden rounded-2xl transition-all duration-300 group",
        "bg-panel-background hover:shadow-2xl",
        "min-h-36 flex flex-col",
      )}
    >
      <img
        className="absolute bottom-0 right-0 opacity-100 w-64 group-hover:brightness-120 transition-all duration-300"
        src={foregroundIcon}
      />
      <div className="px-6 py-4 flex flex-col h-full">
        <div className="flex items-center gap-1 mb-3">
          <h2 className="mobile:text-lg font-semibold">{title}</h2>
          {goToAnotherPage && goToAnotherPageIcon}
        </div>
        <div className="mt-auto space-y-6 z-10">
          <div>
            {primaryStat.value !== 0 && (
              <p className="text-xl font-bold">{primaryStat.value}</p>
            )}
            <p className="text-gray-400 mt-1 text-sm">{primaryStat.label}</p>
          </div>
        </div>
      </div>
    </NavLink>
  );
};

export const Spaces = () => {
  const clubStats = useSelector(clubStatsSelector);
  const canUseClubImport = useSelector(canUseClubImportSelector);

  return (
    <>
      <p className="text-white pl-4 touch-device:pl-0 ml-2 font-semibold">
        Spaces{" "}
        {clubStats.lastImportedAt > 0 && (
          <span className="text-dark-tertiary text-xs ml-2.5">
            <span>Last import: </span>
            {formatTime(Date.now() - clubStats.lastImportedAt)}
            <span> ago</span>
          </span>
        )}
      </p>
      <div className="grid mobile:grid-cols-2 gap-4 px-4 touch-device:px-0">
        <SpaceCard
          onClick={() => window.scrollTo(0, 0)}
          to={canUseClubImport ? "/my-club/players" : "/get-my-players"}
          title="My Players"
          foregroundIcon={
            import.meta.env.VITE_APP_ASSETS_BASE_URL +
            "/fc25/my-club/spaces/my-club.png"
          }
          primaryStat={{
            label: "Players in your club",
            value: canUseClubImport ? clubStats.playersCount : 1342,
          }}
        />
        <SpaceCard
          onClick={() => window.scrollTo(0, 0)}
          to={
            canUseClubImport
              ? "/evolutions/my-evolutions"
              : "/get-my-evolutions"
          }
          title="My Evolutions"
          goToAnotherPage
          foregroundIcon={
            import.meta.env.VITE_APP_ASSETS_BASE_URL +
            "/fc25/my-club/spaces/evolution.png"
          }
          primaryStat={{
            label: "Evolved players in your club",
            value: canUseClubImport ? clubStats.evolvedPlayersCount : 42,
          }}
        />
      </div>
    </>
  );
};
